import React from 'react';
import { useTranslation } from 'react-i18next';
import Btn from '../Btn/Btn';
import SpinnerBox from '../Spinner/SpinnerBox';
import { QuestionnaireSummary } from './types';
import { QuestionnaireKind, isTherapyMaterialKind } from '../../types/QuestionnaireKind';
import './QuestionnaireList.css';

type Props = {
    questionnaires: QuestionnaireSummary[];
    isLoading: boolean;
    error: string | null;
    searchTerm: string;
    onSearchChange: (term: string) => void;
    onAssign: (questionnaire: QuestionnaireSummary) => void;
    onPreview: (questionnaire: QuestionnaireSummary) => void;
    assignmentFeedback: string | null;
    accessToken: string | undefined;
    resourceKind: QuestionnaireKind;
};

const QuestionnaireList = ({
    questionnaires,
    isLoading,
    error,
    searchTerm,
    onSearchChange,
    onAssign,
    onPreview,
    assignmentFeedback,
    accessToken,
    resourceKind,
}: Props): JSX.Element => {
    const { t } = useTranslation();
    const isTherapyMaterial = isTherapyMaterialKind(resourceKind);

    const filteredQuestionnaires = questionnaires.filter((item) => {
        if (!searchTerm.trim()) {
            return true;
        }
        const search = searchTerm.toLowerCase();
        return (
            item.displayName?.toLowerCase().includes(search) ||
            item.name?.toLowerCase().includes(search) ||
            item.title?.toLowerCase().includes(search) ||
            item.id?.toLowerCase().includes(search)
        );
    });

    if (!accessToken) {
        return (
            <div className="frontpage__info-message">
                {isTherapyMaterial ? t('Log in to load therapy material.') : t('Log in to load questionnaires.')}
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="frontpage__loading">
                <SpinnerBox />
                <p>{isTherapyMaterial ? t('Loading therapy material...') : t('Loading questionnaires...')}</p>
            </div>
        );
    }

    return (
        <>
            {questionnaires.length > 0 && (
                <input
                    type="text"
                    className="frontpage__search-input"
                    placeholder={isTherapyMaterial ? t('Search therapy material...') : t('Search questionnaires...')}
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
                />
            )}
            {assignmentFeedback && <div className="frontpage__info-message">{assignmentFeedback}</div>}
            {error && (
                <div className="frontpage__error">
                    {isTherapyMaterial ? t('Failed to load therapy material') : t('Failed to load questionnaires')}:{' '}
                    {error}
                </div>
            )}
            {filteredQuestionnaires.length > 0 ? (
                <ul className="frontpage__list">
                    {filteredQuestionnaires.map((item) => (
                        <li key={item.id} className="frontpage__questionnaire-item">
                            <div
                                className="frontpage__task-content frontpage__task-content--clickable"
                                onClick={() => onAssign(item)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onAssign(item);
                                    }
                                }}
                            >
                                <span className="frontpage__list-title">
                                    {item.displayName}
                                    {item.isPrivate && (
                                        <span className="frontpage__private-badge" title={t('Only visible to you')}>
                                            {t('Private')}
                                        </span>
                                    )}
                                </span>
                                <span className="frontpage__list-meta">
                                    {isTherapyMaterial ? t('Therapy Material') : t('Questionnaire')}: {item.id}
                                </span>
                                {item.title && item.title !== item.displayName && (
                                    <span className="frontpage__list-meta">
                                        {t('Title')}: {item.title}
                                    </span>
                                )}
                                {item.status && (
                                    <span className="frontpage__list-meta">
                                        {t('Status')}: {item.status}
                                    </span>
                                )}
                                {item.updatedAt && (
                                    <span className="frontpage__list-meta">
                                        {t('Updated at')}: {new Date(item.updatedAt).toLocaleString()}
                                    </span>
                                )}
                            </div>
                            <Btn
                                title={t('Preview')}
                                variant="secondary"
                                onClick={() => {
                                    onPreview(item);
                                }}
                            />
                        </li>
                    ))}
                </ul>
            ) : questionnaires.length > 0 ? (
                <div className="frontpage__empty">
                    {isTherapyMaterial ? t('No therapy material found') : t('No questionnaires found')}
                </div>
            ) : (
                <div className="frontpage__empty">
                    {isTherapyMaterial ? t('No therapy material available yet') : t('No questionnaires available yet')}
                </div>
            )}
        </>
    );
};

export default QuestionnaireList;
